/**
 * SearchAddBar — the hero search/scan box for the redesigned billing screen.
 *
 * Sits at the top of the centre column, above the (now central) cart. Typing an
 * item name, SKU, barcode, or HSN opens a dropdown of ranked matches the cashier
 * can pick with the mouse or the keyboard:
 *
 *   ↑ / ↓        move the highlight in the dropdown
 *   Enter        add the highlighted row (or the top match when none is moved)
 *   Esc          clear the box / close the dropdown
 *   / or Ctrl+K  focus the box from anywhere
 *
 * Preserves every billing affordance the old grid search had:
 *   • quantity-on-add — "3 milk", "3*milk", "milk x3" (via {@link parseQtyQuery})
 *   • relevance ranking so Enter targets the intended item (via {@link matchScore})
 *   • USB keyboard-wedge scanner (independent of box focus, via useBarcodeScanner)
 *   • instant auto-add + beep when the text exactly matches a barcode / SKU
 *
 * Cart math and add semantics live in `useCartStore`; this component only
 * resolves the product and calls `onAdd(product, qty)`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Search, CornerDownLeft, Plus } from 'lucide-react';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useCartStore } from '@/store/useCartStore';
import { useObservable } from '@/hooks/useObservable';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useLiveStock } from '@/sync/liveStock';
import { playBeep } from '@/utils/beep';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { parseQtyQuery, matchScore } from '@/billing/catalogSearch';
import { recentManualItems, type RememberedManualItem } from '@/billing/manualItem';

/** Max matches rendered in the dropdown (list scrolls; keyboard nav follows). */
const MAX_RESULTS = 40;

interface Props {
  /** Add a product to the cart. `qty` (default 1) supports "3 milk" input. */
  onAdd: (product: Product, qty?: number) => void;
  /**
   * Open the manual-item form. Passed only when the store allows non-catalog
   * lines, so when the setting is off the search box behaves exactly as it
   * always did. A remembered item arrives here too rather than going straight
   * into the cart: its saved rate is a starting point, not a price list, and
   * the operator confirms it every time.
   */
  onAddManual?: (initialName: string, prefill?: RememberedManualItem) => void;
}

/** Bold the matched substring in a product name so the eye confirms the hit. */
function HighlightName({ name, q }: { name: string; q: string }) {
  if (!q) return <>{name}</>;
  const i = name.toLowerCase().indexOf(q);
  if (i < 0) return <>{name}</>;
  return (
    <>
      {name.slice(0, i)}
      <span className="font-semibold">{name.slice(i, i + q.length)}</span>
      {name.slice(i + q.length)}
    </>
  );
}

/**
 * One actionable row in the dropdown. Products and non-catalog items share a
 * list so the arrow keys and Enter do not have to know the difference.
 */
type SearchOption =
  | { kind: 'product'; product: Product }
  | { kind: 'remembered'; item: RememberedManualItem }
  | { kind: 'new'; name: string };

export function SearchAddBar({ onAdd, onAddManual }: Props) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const signalScan = useCartStore((s) => s.signalScan);

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Audio + cart-row-flash confirmation for a scan-driven add.
  const scanFeedback = (productId: string) => {
    signalScan(productId);
    playBeep();
  };

  // Debounce the search term (quantity prefix stripped — it isn't part of the name).
  useEffect(() => {
    const t = setTimeout(
      () => setSearchQuery(parseQtyQuery(searchInput).term.toLowerCase()),
      150,
    );
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reactive product read — name / barcode / SKU / HSN, active + in-store only.
  const products = useObservable<Product[]>(
    () => {
      if (!activeStoreId || searchQuery.length === 0) return null;
      const like = `%${searchQuery}%`;
      return database
        .get<Product>('products')
        .query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.where('is_active', true),
          Q.or(
            Q.where('name', Q.like(like)),
            Q.where('barcode', Q.like(like)),
            Q.where('sku', Q.like(like)),
            Q.where('hsn_code', Q.like(like)),
          ),
          Q.sortBy('name', Q.asc),
        )
        .observeWithColumns(['current_stock', 'selling_price', 'name']);
    },
    [activeStoreId, searchQuery],
    [],
  );

  // Rank so the Enter target (index 0) is almost always the intended item.
  const ranked = useMemo(() => {
    if (!searchQuery) return [];
    return [...products]
      .sort((a, b) => {
        const d = matchScore(a, searchQuery) - matchScore(b, searchQuery);
        return d !== 0 ? d : a.name.localeCompare(b.name);
      })
      .slice(0, MAX_RESULTS);
  }, [products, searchQuery]);

  // Manual items this store has billed before. Only looked up when the store
  // allows them, and only while something is typed.
  const [remembered, setRemembered] = useState<RememberedManualItem[]>([]);
  useEffect(() => {
    if (!onAddManual || !activeStoreId || searchQuery.length === 0) {
      setRemembered([]);
      return;
    }
    let alive = true;
    void recentManualItems(activeStoreId, searchQuery, 4)
      .then((r) => { if (alive) setRemembered(r); })
      .catch(() => { if (alive) setRemembered([]); });
    return () => { alive = false; };
  }, [onAddManual, activeStoreId, searchQuery]);

  /**
   * Everything the dropdown can act on, in the order it is shown, so that the
   * arrow keys and Enter move through one list rather than three.
   *
   * A remembered manual item whose name a real product now carries is dropped:
   * once the shop has catalogued something, the catalogue entry is the answer.
   */
  const options = useMemo((): SearchOption[] => {
    const productNames = new Set(ranked.map((p) => p.name.trim().toLowerCase()));
    const out: SearchOption[] = ranked.map((product) => ({ kind: 'product' as const, product }));
    for (const item of remembered) {
      if (!productNames.has(item.name.trim().toLowerCase())) {
        out.push({ kind: 'remembered', item });
      }
    }
    const typed = parseQtyQuery(searchInput).term.trim();
    // The escape hatch, always last: nothing here is what I am selling.
    if (onAddManual && typed.length > 0) {
      const alreadyOffered = out.some(
        (o) =>
          (o.kind === 'product' ? o.product.name : o.kind === 'remembered' ? o.item.name : '')
            .trim()
            .toLowerCase() === typed.toLowerCase(),
      );
      if (!alreadyOffered) out.push({ kind: 'new', name: typed });
    }
    return out;
  }, [ranked, remembered, searchInput, onAddManual]);

  const open = focused && searchInput.trim().length > 0;

  // Reset the highlight whenever the result set changes.
  useEffect(() => {
    setHighlight(0);
  }, [searchQuery]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // Global focus shortcuts: `/` and Ctrl/Cmd+K.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (e.key === '/' && !inField) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const add = (p: Product) => {
    onAdd(p, parseQtyQuery(searchInput).qty);
    scanFeedback(p.id);
    setSearchInput('');
    setHighlight(0);
    searchRef.current?.focus();
  };

  /**
   * The token this effect has already auto-added.
   *
   * Without it, scanning anything crashed the till with "Maximum update depth
   * exceeded". `searchQuery` is debounced 150ms behind `searchInput`, so the
   * `setSearchInput('')` below does NOT clear it straight away — and in that
   * window the add re-renders BillingScreen, which hands this component a
   * fresh `onAdd` closure, which is in the dep array, which re-runs the effect
   * against a `searchQuery` that still holds the barcode. It adds again, which
   * re-renders again, until React gives up.
   *
   * Keying on the token rather than on `onAdd` identity makes the effect
   * idempotent per scan, so it survives any amount of re-render churn.
   */
  const autoAddedFor = useRef<string | null>(null);

  // Auto-add when the typed/scanned text exactly matches a barcode or SKU.
  useEffect(() => {
    if (!searchQuery) {
      autoAddedFor.current = null;
      return;
    }
    if (autoAddedFor.current === searchQuery) return;
    const exact = products.find(
      (p) =>
        (p.barcode && p.barcode.toLowerCase() === searchQuery) ||
        (p.sku && p.sku.toLowerCase() === searchQuery),
    );
    if (exact) {
      autoAddedFor.current = searchQuery;
      onAdd(exact, parseQtyQuery(searchInput).qty);
      scanFeedback(exact.id);
      setSearchInput('');
      setHighlight(0);
      searchRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, products, onAdd]);

  // USB keyboard-wedge scanner — looks the code up directly, independent of the
  // box, so a scan works whether or not the input has focus.
  useBarcodeScanner({
    enabled: !!activeStoreId,
    onScan: async (code) => {
      if (!activeStoreId) return;
      try {
        const matches = await database
          .get<Product>('products')
          .query(
            Q.where('store_id', activeStoreId),
            Q.where('is_deleted', false),
            Q.where('is_active', true),
            Q.or(Q.where('barcode', code), Q.where('sku', code)),
          )
          .fetch();
        if (matches.length > 0) {
          const exact =
            matches.find((m) => m.barcode === code) ??
            matches.find((m) => m.sku === code) ??
            matches[0];
          onAdd(exact);
          scanFeedback(exact.id);
          setSearchInput('');
          return;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[SearchAddBar] barcode lookup failed:', err);
      }
      // Not found — surface it in the box so the cashier sees what was scanned.
      setSearchInput(code);
      searchRef.current?.focus();
    },
  });

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (searchInput) {
        e.preventDefault();
        setSearchInput('');
      }
      return;
    }
    if (!open || options.length === 0) {
      // Enter with results still resolving / none rendered — nothing to add.
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(options[highlight] ?? options[0]);
    }
  };

  /**
   * Act on one row. A catalog product goes straight into the cart; anything
   * not in the catalog opens the form instead, because its quantity, rate and
   * slab are decisions for this bill rather than facts about a product.
   */
  const pick = (option: SearchOption | undefined) => {
    if (!option) return;
    if (option.kind === 'product') {
      add(option.product);
      return;
    }
    if (option.kind === 'remembered') {
      onAddManual?.(option.item.name, option.item);
    } else {
      onAddManual?.(option.name);
    }
    setSearchInput('');
    setHighlight(0);
    searchRef.current?.focus();
  };

  if (!activeStoreId) {
    return <div className="text-fg-secondary text-body">No active store. Sign in again.</div>;
  }

  const parsedQty = parseQtyQuery(searchInput).qty;

  return (
    <div className="relative">
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
        <Input
          ref={searchRef}
          autoFocus
          placeholder="Scan or search — name, SKU, barcode or HSN   ( / to focus )"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="h-12 pl-10 pr-24 text-body"
          role="combobox"
          aria-expanded={open}
          aria-controls="search-add-results"
          aria-autocomplete="list"
        />
        {parsedQty > 1 && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-accent/10 px-2 py-0.5 text-label uppercase text-accent-ink tabular-nums">
            × {parsedQty}
          </span>
        )}
      </div>

      {open && (
        <div
          id="search-add-results"
          className="absolute inset-x-0 top-full z-40 mt-1.5 overflow-hidden rounded-lg border border-stroke-hairline/15 bg-bg-surface shadow-e2"
          // Keep the input focused while clicking a row (so onClick fires).
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.length === 0 ? (
            <div className="px-4 py-6 text-center text-body text-fg-tertiary">
              No matches for “{parseQtyQuery(searchInput).term}”.
            </div>
          ) : (
            <>
              <div ref={listRef} className="max-h-[min(60vh,420px)] overflow-auto py-1">
                {options.map((option, idx) =>
                  option.kind === 'product' ? (
                    <ResultRow
                      key={option.product.id}
                      product={option.product}
                      query={searchQuery}
                      highlighted={idx === highlight}
                      index={idx}
                      onHover={() => setHighlight(idx)}
                      onPick={() => pick(option)}
                    />
                  ) : (
                    <ManualRow
                      key={option.kind === 'remembered' ? `r:${option.item.name}` : 'new'}
                      label={option.kind === 'remembered' ? option.item.name : option.name}
                      detail={
                        option.kind === 'remembered'
                          ? [
                              option.item.hsn ? `HSN ${option.item.hsn}` : null,
                              `${option.item.gstRate}% GST`,
                              `${formatCurrency(option.item.rate)} / ${option.item.unit}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : 'Enter the details on the next screen'
                      }
                      isNew={option.kind === 'new'}
                      highlighted={idx === highlight}
                      index={idx}
                      onHover={() => setHighlight(idx)}
                      onPick={() => pick(option)}
                    />
                  ),
                )}
              </div>
              <div className="flex items-center justify-between border-t border-stroke-hairline/10 px-3 py-1.5 text-label uppercase text-fg-tertiary">
                <span>
                  {ranked.length} in stock
                  {options.length > ranked.length && ` · ${options.length - ranked.length} not in inventory`}
                </span>
                <span className="inline-flex items-center gap-1">
                  <CornerDownLeft size={12} /> Enter to add · ↑↓ to move
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A row for something the catalog does not have: either one this store has
 * billed before, or the offer to describe a new one. Deliberately quieter than
 * a product row and explicitly labelled, so nobody bills a manual line
 * believing it came out of stock.
 */
function ManualRow({
  label,
  detail,
  isNew,
  highlighted,
  index,
  onHover,
  onPick,
}: {
  label: string;
  detail: string;
  isNew: boolean;
  highlighted: boolean;
  index: number;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      data-idx={index}
      onMouseEnter={onHover}
      onClick={onPick}
      className={cn(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
        highlighted ? 'bg-accent/10' : 'hover:bg-bg-subtle',
      )}
    >
      <Plus size={14} className="shrink-0 text-accent-ink" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-body text-fg-primary">
          {isNew ? <>Bill “{label}” as a manual item</> : label}
        </div>
        <div className="truncate text-label text-fg-tertiary">{detail}</div>
      </div>
      <span className="shrink-0 rounded border border-stroke-hairline/20 px-1.5 py-0.5 text-label uppercase text-fg-tertiary">
        Not in inventory
      </span>
    </button>
  );
}

function ResultRow({
  product,
  query,
  highlighted,
  index,
  onHover,
  onPick,
}: {
  product: Product;
  query: string;
  highlighted: boolean;
  index: number;
  onHover: () => void;
  onPick: () => void;
}) {
  const displayStock = useLiveStock(product.id, product.currentStock);
  const tracked = product.trackInventory && !product.isService;
  const oos = tracked && displayStock <= 0;
  const lowStock = tracked && displayStock <= product.lowStockAlert;
  const code = product.sku || product.barcode || product.hsnCode || '';

  return (
    <button
      type="button"
      data-idx={index}
      disabled={oos}
      onMouseEnter={onHover}
      onClick={onPick}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
        highlighted ? 'bg-accent/10' : 'hover:bg-bg-subtle',
        oos && 'cursor-not-allowed opacity-50',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-body text-fg-primary">
          <HighlightName name={product.name} q={query} />
        </div>
        {code && (
          <div className="truncate text-label uppercase text-fg-tertiary/80 tabular-nums">
            {code}
          </div>
        )}
      </div>
      {tracked && (
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-label tabular-nums',
            oos
              ? 'bg-danger/10 text-danger-ink'
              : lowStock
                ? 'bg-warning/10 text-warning-ink'
                : 'bg-bg-subtle text-fg-tertiary',
          )}
        >
          {oos ? 'Out of stock' : `${displayStock} in stock`}
        </span>
      )}
      <span className="w-20 shrink-0 text-right text-body font-semibold text-fg-primary tabular-nums">
        {formatCurrency(product.sellingPrice)}
      </span>
    </button>
  );
}
